import LoadingButton from '@mui/lab/LoadingButton';
import { Autocomplete, Box, Button, Skeleton, TextField } from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { nanoid } from '@reduxjs/toolkit';
import BaseModal, { BaseModalRef } from 'modals/BaseModal';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'utilities/hooks';
import UsersAutocomplete from 'components/UsersAutocomplete';
import { fetchUsers } from 'slices/usersActions';
import { setParams, clearParams, FiltersState } from 'slices/documentsSlice';

export interface FilterModalProps {
    onSubmit?: (filters?: any) => void;
    onClose?: () => void;
}

const FilterModal = (props: FilterModalProps, ref: React.Ref<unknown>) => {
    const { params, breadcrumbs } = useAppSelector((state) => state.documents);
    const modal = useRef<BaseModalRef>(null);
    const dispatch = useAppDispatch();
    const { onSubmit } = props;
    const { handleSubmit } = useForm();
    const [isOpen, setIsOpen] = useState(false);
    const formId = 'category_create_form_' + nanoid();
    const { loading: UsersLoading } = useAppSelector((state) => state.users);
    const [usersSelected, setUsersSelected] = useState<any>([]);
    const [updatedUsersSelected, setUpdatedUsersSelected] = useState<any>([]);
    const [type, setType] = useState<any>([]);
    const [modified, setModified] = useState<any>(null);
    const [customModified, setCustomModified] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    const [created, setCreated] = useState<any>(null);
    const [customCreated, setCustomCreated] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    const [logic, setLogic] = useState<'and' | 'or'>('and');
    const [hasVersions, setHasVersions] = useState<boolean>(false);
    const [inDescendantsOf, setInDescendantsOf] = useState<number | null>(null);

    useEffect(() => {
        if (isOpen) {
            if (params?.filters) {
                setType(params?.filters?.type?.value || []);
                setUsersSelected(Object.values(params?.filters?.created_by?.value || []));
                setUpdatedUsersSelected(Object.values(params?.filters?.updated_by?.value || []));
                setModified(params?.filters?.modified || null);
                setCreated(params?.filters?.created || null);
                setLogic(params?.filters?.logic || 'and');
                setHasVersions(!!params?.filters?.has_versions?.value);
                if (
                    (params?.filters?.modified_after || params?.filters?.modified_before) &&
                    params?.filters?.modified?.id === 'custom'
                ) {
                    let modified_after: Date | null = null;
                    let modified_before: Date | null = null;
                    if (params?.filters?.modified_after?.value) {
                        let [year, month, day] = params?.filters?.modified_after?.value.split('-');
                        modified_after = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                    }
                    if (params?.filters?.modified_before?.value) {
                        let [year, month, day] = params?.filters?.modified_before?.value.split('-');
                        modified_before = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                    }
                    setCustomModified([
                        modified_after ? dayjs(modified_after) : null,
                        modified_before ? dayjs(modified_before) : null,
                    ]);
                }
                if (
                    (params?.filters?.created_after || params?.filters?.created_before) &&
                    params?.filters?.created?.id === 'custom'
                ) {
                    let created_after: Date | null = null;
                    let created_before: Date | null = null;
                    if (params?.filters?.created_after?.value) {
                        let [year, month, day] = params?.filters?.created_after?.value.split('-');
                        created_after = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                    }
                    if (params?.filters?.created_before?.value) {
                        let [year, month, day] = params?.filters?.created_before?.value.split('-');
                        created_before = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                    }
                    setCustomCreated([
                        created_after ? dayjs(created_after) : null,
                        created_before ? dayjs(created_before) : null,
                    ]);
                }
                setInDescendantsOf(params?.filters?.in_descendants_of?.value || null);
            }
        }
    }, [isOpen]);

    const handleClose = () => {
        props.onClose?.();
    };

    const applyFilters = () => {
        var data: FiltersState = {};

        if (type && type.length) {
            data.type = {
                label: 'Type',
                value: type,
            };
        }
        if (usersSelected && usersSelected.length) {
            data.created_by = {
                label: 'Created By',
                value: usersSelected,
            };
        }
        if (updatedUsersSelected && updatedUsersSelected.length) {
            data.updated_by = {
                label: 'Modified By',
                value: updatedUsersSelected,
            };
        }
        if (modified) {
            let date = new Date();
            let modified_after,
                modified_before: Date | null = null;
            switch (modified.id) {
                case 'today':
                    modified_after = date;
                    break;
                case 'last7days':
                    date.setDate(date.getDate() - 7);
                    modified_after = date;
                    break;
                case 'last30days':
                    date.setDate(date.getDate() - 30);
                    modified_after = date;
                    break;
                case 'last90days':
                    date.setDate(date.getDate() - 90);
                    modified_after = date;
                    break;
                case 'custom':
                    if (!customModified[0] || !customModified[1]) {
                        alert('Custom modified date range must have both dates choosen');
                        return;
                    }
                    modified_after = customModified[0]?.toDate();
                    modified_before = customModified[1]?.toDate();
                    break;
            }
            if (modified_after) {
                let year = modified_after.getFullYear();
                let month = String(modified_after.getMonth() + 1).padStart(2, '0');
                let day = String(modified_after.getDate()).padStart(2, '0');
                data.modified_after = {
                    label: 'Modified after',
                    value: `${year}-${month}-${day}`,
                };
            }
            if (modified_before) {
                let year = modified_before.getFullYear();
                let month = String(modified_before.getMonth() + 1).padStart(2, '0');
                let day = String(modified_before.getDate()).padStart(2, '0');
                data.modified_before = {
                    label: 'Modified before',
                    value: `${year}-${month}-${day}`,
                };
            }
            data.modified = modified;
        }
        if (created) {
            let date = new Date();
            let created_after,
                created_before: Date | null = null;
            switch (created.id) {
                case 'today':
                    created_after = date;
                    break;
                case 'last7days':
                    date.setDate(date.getDate() - 7);
                    created_after = date;
                    break;
                case 'last30days':
                    date.setDate(date.getDate() - 30);
                    created_after = date;
                    break;
                case 'last90days':
                    date.setDate(date.getDate() - 90);
                    created_after = date;
                    break;
                case 'custom':
                    if (!customCreated[0] || !customCreated[1]) {
                        alert('Custom created date range must have both dates chosen');
                        return;
                    }
                    created_after = customCreated[0]?.toDate();
                    created_before = customCreated[1]?.toDate();
                    break;
            }
            if (created_after) {
                let year = created_after.getFullYear();
                let month = String(created_after.getMonth() + 1).padStart(2, '0');
                let day = String(created_after.getDate()).padStart(2, '0');
                data.created_after = {
                    label: 'Created after',
                    value: `${year}-${month}-${day}`,
                };
            }
            if (created_before) {
                let year = created_before.getFullYear();
                let month = String(created_before.getMonth() + 1).padStart(2, '0');
                let day = String(created_before.getDate()).padStart(2, '0');
                data.created_before = {
                    label: 'Created before',
                    value: `${year}-${month}-${day}`,
                };
            }
            data.created = created;
        }

        if (hasVersions) {
            data.has_versions = {
                label: 'Multiple Versions',
                value: true,
            };
        }

        if (inDescendantsOf !== null) {
            data.in_descendants_of = {
                label: 'Within',
                value: inDescendantsOf,
            };
        }

        if (logic && Object.keys(data).length > 1) {
            data.logic = logic;
        }

        dispatch(setParams({ filters: data as FiltersState }));
        onSubmit?.();
        close();
    };

    const open = () => {
        modal?.current?.open();
    };

    const close = () => {
        modal?.current?.close();
    };

    const clearFilters = () => {
        setType([]);
        setUsersSelected([]);
        setUpdatedUsersSelected([]);
        setModified(null);
        setCustomModified([null, null]);
        setCreated(null);
        setCustomCreated([null, null]);
        setLogic('and');
        setHasVersions(false);
        setInDescendantsOf(null);
    };

    const onUsersDebounce = (value: string, callback: (value: any) => void) => {
        dispatch(fetchUsers({ search: value }))
            .unwrap()
            .then((response: any) => {
                callback(response.data);
            })
            .catch(() => {});
    };

    const handleUsersSelected = (users: any[]) => {
        setUsersSelected(users);
    };
    const handleUpdatedUsersSelected = (users: any[]) => {
        setUpdatedUsersSelected(users);
    };

    useImperativeHandle(ref, () => ({
        open: () => open(),
        close: () => close(),
    }));

    const typesOptions = [
        { id: 'image', label: 'Photos & Images' },
        { id: 'pdf', label: 'PDFs' },
        { id: 'txt', label: 'Documents' },
        { id: 'xsl', label: 'Spreadsheet' },
        { id: 'pwp', label: 'Presentation' },
        { id: 'media', label: 'Audio / Videos' },
        { id: 'zip', label: 'Archives (zip)' },
    ];

    const modifiedOptions = [
        { id: 'today', label: 'Today' },
        { id: 'last7days', label: 'Last 7 days' },
        { id: 'last30days', label: 'Last 30 days' },
        { id: 'last90days', label: 'Last 90 days' },
        { id: 'custom', label: 'Custom' },
    ];

    const createdOptions = [
        { id: 'today', label: 'Today' },
        { id: 'last7days', label: 'Last 7 days' },
        { id: 'last30days', label: 'Last 30 days' },
        { id: 'last90days', label: 'Last 90 days' },
        { id: 'custom', label: 'Custom' },
    ];

    return (
        <>
            <BaseModal
                ref={modal}
                title={'Filters'}
                maxWidth={'md'}
                open={isOpen}
                setOpen={setIsOpen}
                onClose={handleClose}
                actions={
                    <>
                        <Button color="primary" disableElevation onClick={close}>
                            Cancel
                        </Button>
                        <Button color="primary" disableElevation onClick={() => clearFilters()}>
                            Clear filters
                        </Button>
                        <LoadingButton type="submit" form={formId} color="primary" disableElevation>
                            Apply
                        </LoadingButton>
                    </>
                }
            >
                {isOpen ? (
                    <form id={formId} onSubmit={handleSubmit(applyFilters)}>
                        <ToggleButtonGroup
                            exclusive
                            value={logic}
                            onChange={(e, val) => val && setLogic(val)}
                            size="small"
                            sx={{ mb: 1 }}
                        >
                            <ToggleButton value="and">AND</ToggleButton>
                            <ToggleButton value="or">OR</ToggleButton>
                        </ToggleButtonGroup>
                        <Autocomplete
                            multiple
                            noOptionsText="No options found..."
                            options={typesOptions}
                            renderInput={(params) => <TextField {...params} label={'Type'} />}
                            value={type}
                            onChange={(event: any, newValue: any) => {
                                setType(newValue);
                            }}
                            isOptionEqualToValue={(option: any, value: any) => {
                                return option.id === value.id;
                            }}
                            sx={{ pb: 1 }}
                        />
                        <UsersAutocomplete
                            label="Created by"
                            debounceOnOpen={true}
                            dependent={usersSelected}
                            valueSet={usersSelected}
                            onDebounce={onUsersDebounce}
                            onSelect={handleUsersSelected}
                            loading={UsersLoading}
                            sx={{ pb: 1 }}
                        />
                        <UsersAutocomplete
                            label="Modified by"
                            debounceOnOpen={true}
                            dependent={updatedUsersSelected}
                            valueSet={updatedUsersSelected}
                            onDebounce={onUsersDebounce}
                            onSelect={handleUpdatedUsersSelected}
                            loading={UsersLoading}
                            sx={{ pb: 1 }}
                        />
                        <Autocomplete
                            noOptionsText="No options found..."
                            options={modifiedOptions}
                            renderInput={(params) => <TextField {...params} label={'Last Modified'} />}
                            value={modified}
                            onChange={(event: any, newValue: any) => {
                                setModified(newValue);
                            }}
                            isOptionEqualToValue={(option: any, value: any) => {
                                return option.id === value.id;
                            }}
                        />
                        {modified && modified.id === 'custom' && (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Box sx={{ display: 'flex', gap: 2, pt: 1 }}>
                                    <DatePicker
                                        label="From"
                                        value={customModified[0]}
                                        onChange={(newValue) => setCustomModified([newValue, customModified[1]])}
                                        sx={{ flex: 1 }}
                                    />
                                    <DatePicker
                                        label="To"
                                        value={customModified[1]}
                                        onChange={(newValue) => setCustomModified([customModified[0], newValue])}
                                        sx={{ flex: 1 }}
                                    />
                                </Box>
                            </LocalizationProvider>
                        )}
                        <Autocomplete
                            noOptionsText="No options found..."
                            options={createdOptions}
                            renderInput={(params) => <TextField {...params} label={'Created'} />}
                            value={created}
                            onChange={(event: any, newValue: any) => {
                                setCreated(newValue);
                            }}
                            isOptionEqualToValue={(option: any, value: any) => {
                                return option.id === value.id;
                            }}
                            sx={{ mt: 1 }}
                        />
                        {created && created.id === 'custom' && (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Box sx={{ display: 'flex', gap: 2, pt: 1 }}>
                                    <DatePicker
                                        label="From"
                                        value={customCreated[0]}
                                        onChange={(newValue) => setCustomCreated([newValue, customCreated[1]])}
                                        sx={{ flex: 1 }}
                                    />
                                    <DatePicker
                                        label="To"
                                        value={customCreated[1]}
                                        onChange={(newValue) => setCustomCreated([customCreated[0], newValue])}
                                        sx={{ flex: 1 }}
                                    />
                                </Box>
                            </LocalizationProvider>
                        )}
                        <FormControlLabel
                            control={<Switch checked={hasVersions} onChange={(e) => setHasVersions(e.target.checked)} />}
                            label="Multiple Versions"
                            sx={{ mt: 1 }}
                        />
                        {(() => {
                            const currentFolderId = (params.parent_id !== undefined && params.parent_id !== null)
                                ? Number(params.parent_id)
                                : (breadcrumbs && breadcrumbs.length ? breadcrumbs[breadcrumbs.length - 1].id : null);
                            const hasCurrentFolder = currentFolderId !== null && currentFolderId !== undefined;
                            return (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={inDescendantsOf !== null}
                                            disabled={!hasCurrentFolder}
                                            onChange={(e) => {
                                                if (e.target.checked && hasCurrentFolder) {
                                                    setInDescendantsOf(Number(currentFolderId));
                                                } else {
                                                    setInDescendantsOf(null);
                                                }
                                            }}
                                        />
                                    }
                                    label="Search within this folder and its subfolders"
                                    sx={{ mt: 1 }}
                                />
                            );
                        })()}
                    </form>
                ) : (
                    <Skeleton variant="rounded" width={396} height={56} />
                )}
            </BaseModal>
        </>
    );
};

export default forwardRef(FilterModal);
